import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  List,
  Typography,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { styled } from "@mui/material/styles";
import { selectLeaderboardPlayers, removePlayerFromGame } from "../../leaderboard/leaderboardSlice";
import { selectGameStatusObject, selectGameId } from "../../gamestate/gameStateSlice";
import { makeSelectPlayersReadinessForInterRound, selectPlayersReadinessForInstructions } from "../../player/playerSlice";
import { getInterRoundPhase, PLAYER_STATES, PLAYER_PHASES } from "../../player/playerConstants";
import { GAMESTATE_LOBBY, GAMESTATE_INSTRUCTIONS } from "../../gamestate/constants";
import Provider from "provider";

const Root = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const HighlightedText = styled('span')({
  color: "#e7ae02",
  fontWeight: "bold",
  fontStyle: "italic",
});

const AdminLeaderboardRow = ({ player, index, inControl, showReady, isReady, readyPhase }) => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(player.score);
  const [playerDeleting, setPlayerDeleting] = useState(false);

  const toggleReady = async () => {
    const isInstructions = readyPhase === PLAYER_PHASES.INSTRUCTIONS;
    const newState = isReady
      ? { state: isInstructions ? PLAYER_STATES.INSTRUCTIONS.READING : PLAYER_STATES.INTER_ROUND.WAITING }
      : { state: isInstructions ? PLAYER_STATES.INSTRUCTIONS.READY : PLAYER_STATES.INTER_ROUND.READY };
    await Provider.updatePlayerState(gameId, player.id, readyPhase, newState);
  };

  const removePlayer = () => {
    setPlayerDeleting(true);
    dispatch(removePlayerFromGame(gameId, player.id));
  };

  const startEdit = () => {
    setEditValue(player.score);
    setEditing(true);
  };

  const saveEdit = async () => {
    const newScore = parseInt(editValue, 10);
    if (!isNaN(newScore) && newScore !== player.score) {
      await Provider.updatePlayerScore(gameId, player.id, newScore);
    }
    setEditing(false);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const nameDisplay = inControl
    ? <HighlightedText>{`${index + 1}. ${player.name}`}</HighlightedText>
    : `${index + 1}. ${player.name}`;

  const scoreDisplay = editing ? (
    <TextField
      type="number"
      size="small"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={saveEdit}
      onKeyDown={handleKeyDown}
      autoFocus
      variant="standard"
      sx={{ width: 100 }}
    />
  ) : (
    <span>
      {"$" + player.score.toLocaleString()}
      <IconButton size="small" onClick={startEdit} sx={{ ml: 0.5 }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </span>
  );

  return (
    <ListItem
      secondaryAction={
        <>
          {showReady && (
            <IconButton
              edge="end"
              aria-label="toggle ready"
              onClick={toggleReady}
              sx={{ color: isReady ? '#4caf50' : '#bdbdbd', mr: 1 }}
            >
              {isReady ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
            </IconButton>
          )}
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={removePlayer}
            disabled={playerDeleting}
          >
            <DeleteIcon />
          </IconButton>
        </>
      }
    >
      <ListItemText
        primary={nameDisplay}
        secondary={scoreDisplay}
      />
    </ListItem>
  );
};

const AdminLeaderboard = () => {
  const players = useSelector(selectLeaderboardPlayers);
  const gameState = useSelector(selectGameStatusObject);
  const { playerInControl } = gameState;

  const isLobby = gameState.currentState === GAMESTATE_LOBBY && !!gameState.round;
  const isInstructions = gameState.currentState === GAMESTATE_INSTRUCTIONS;
  const showReady = isLobby || isInstructions;
  const round = gameState.round;

  const selectReadiness = useMemo(() => {
    if (isLobby && round) return makeSelectPlayersReadinessForInterRound(round);
    if (isInstructions) return selectPlayersReadinessForInstructions;
    return () => ({});
  }, [isLobby, isInstructions, round]);
  const readiness = useSelector(selectReadiness);

  const readyPhase = isInstructions ? PLAYER_PHASES.INSTRUCTIONS : getInterRoundPhase(round);

  let list;
  if (!players) {
    list = <Typography>Loading...</Typography>;
  } else {
    list = players.map((player, idx) => (
      <AdminLeaderboardRow
        player={player}
        index={idx}
        key={`player-${player.id}`}
        inControl={player.id === playerInControl}
        showReady={showReady}
        isReady={readiness[player.id]?.isReady || false}
        readyPhase={readyPhase}
      />
    ));
  }

  const dense = players && players.length > 8;

  return (
    <Root>
      <Typography variant="h6">Leaderboard</Typography>
      <List dense={dense}>{list}</List>
    </Root>
  );
};

export default AdminLeaderboard;
